'use client'

import { Dialog, DialogContent } from '@qr-front/ui/components/Dialog'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { useAtom, useAtomValue } from 'jotai'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import { useBus } from 'react-bus'

import { LazyToastContainer } from '@/components/LazyToastContainer'
import { ROUTES } from '@/constants'
import { getPathname, usePathname } from '@/i18n-routing'
import { getStore } from '@/store'
import { THideSocials } from '@/utils'

import { posthogClient } from '../../../../lib/analytics/posthog'
import { SignUpContainer } from '../../../auth/SignUpContainer'
import {
	getBuilderSessionId,
	isSignUpDialogOpenAtom,
	qrTypeAtom,
	signUpDefaultEmailAtom,
} from '../../generatorState'
import { RESET_NEXT_BUTTON_LOADING_EVENT } from '../GeneratorFooter/NextButton'

const DEFAULT_DIALOG_VERTICAL_PADDING = 40
const CENTERED_DIALOG_MIN_VERTICAL_MARGIN = 44

type TSignUpDialogStyle = CSSProperties & {
	'--sign-up-dialog-y-padding'?: string
}

export const SignUpDialog = ({
	hideSocials,
	hidePassword,
	isAuthenticated,
}: {
	hideSocials: THideSocials
	hidePassword: boolean
	isAuthenticated: boolean
}) => {
	const t = useTranslations()
	const locale = useLocale()
	const bus = useBus()
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const [isOpen, setIsOpen] = useAtom(isSignUpDialogOpenAtom)
	const storedDefaultEmail = useAtomValue(signUpDefaultEmailAtom)
	const qrId = searchParams.get('id')
	const storedEmailForQr =
		qrId && storedDefaultEmail?.qrId === qrId ? storedDefaultEmail : null
	const defaultEmail =
		storedEmailForQr?.draftEmail ?? storedEmailForQr?.previousEmail
	const previousEmail = storedEmailForQr?.previousEmail
	const isSignupRoute = pathname === ROUTES.generator.signup
	const designStepUrl = getPathname({
		locale,
		href: ROUTES.generator.designStep,
	})
	const signupUrl = getPathname({
		locale,
		href: ROUTES.generator.signup,
	})
	const isManuallyClosingRef = useRef(false)
	const [dialogVerticalPadding, setDialogVerticalPadding] = useState(
		DEFAULT_DIALOG_VERTICAL_PADDING,
	)
	const [shouldScrollDialogOverlay, setShouldScrollDialogOverlay] =
		useState(true)
	const [dialogBodyElement, setDialogBodyElement] =
		useState<HTMLDivElement | null>(null)

	// Keep the dialog open during post-signup redirect; reset when leaving the generator layout.
	useEffect(() => {
		return () => {
			setIsOpen(false)
		}
	}, [setIsOpen])

	useEffect(() => {
		if (!isOpen) return
		const authMethods = ['email']
		if (!hideSocials.GOOGLE) authMethods.push('google')
		if (!hideSocials.FACEBOOK) authMethods.push('facebook')
		if (!hideSocials.APPLE) authMethods.push('apple')
		posthogClient.capture('view_signup_form', {
			auth_surface: 'modal',
			builder_session_id: getBuilderSessionId(),
			form_trigger: 'qr_download',
			qr_type: getStore().get(qrTypeAtom),
			auth_methods: authMethods.join(','),
			section: 'modal',
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) return

		const onPopState = () => {
			setIsOpen(false)
			bus.emit(RESET_NEXT_BUTTON_LOADING_EVENT)
		}

		window.addEventListener('popstate', onPopState)
		return () => window.removeEventListener('popstate', onPopState)
	}, [bus, isOpen, setIsOpen])

	useEffect(() => {
		if (!isSignupRoute) {
			isManuallyClosingRef.current = false
			return
		}
		if (isManuallyClosingRef.current) {
			return
		}
		if (isAuthenticated && !isOpen) {
			window.history.replaceState(
				window.history.state,
				'',
				`${designStepUrl}${window.location.search}`,
			)
			return
		}
		setIsOpen(true)
	}, [designStepUrl, isAuthenticated, isOpen, isSignupRoute, setIsOpen])

	useEffect(() => {
		if (!isOpen || !dialogBodyElement) {
			setDialogVerticalPadding(DEFAULT_DIALOG_VERTICAL_PADDING)
			return
		}

		const closeButtonElement = dialogBodyElement
			.closest('[role="dialog"]')
			?.querySelector<HTMLElement>('[data-testid="dialog-close-button"]')

		const updateDialogVerticalPadding = () => {
			const viewportHeight = window.innerHeight
			const dialogHeight =
				dialogBodyElement.offsetHeight +
				(closeButtonElement?.offsetHeight ?? 0)
			const shouldCenterDialog =
				viewportHeight >
				dialogHeight + 2 * CENTERED_DIALOG_MIN_VERTICAL_MARGIN
			const nextPadding = shouldCenterDialog
				? Math.max(
						DEFAULT_DIALOG_VERTICAL_PADDING,
						Math.floor((viewportHeight - dialogHeight) / 2),
					)
				: DEFAULT_DIALOG_VERTICAL_PADDING

			setDialogVerticalPadding(nextPadding)
			setShouldScrollDialogOverlay(!shouldCenterDialog)
		}

		updateDialogVerticalPadding()
		window.addEventListener('resize', updateDialogVerticalPadding)
		const resizeObserver = new ResizeObserver(updateDialogVerticalPadding)
		resizeObserver.observe(dialogBodyElement)
		if (closeButtonElement) resizeObserver.observe(closeButtonElement)

		return () => {
			window.removeEventListener('resize', updateDialogVerticalPadding)
			resizeObserver.disconnect()
		}
	}, [dialogBodyElement, isOpen])

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			// MYQRCODE-10778 AC#13: Radix's onOpenChange doesn't say which
			// dismissal fired — the X button or Escape (overlay click is
			// disabled below via onInteractOutside/onPointerDownOutside).
			// close_button is the common case; Escape would be mislabeled.
			posthogClient.capture('close_signup_form', {
				auth_surface: 'modal',
				builder_session_id: getBuilderSessionId(),
				close_method: 'close_button',
			})
			if (window.location.pathname === signupUrl) {
				isManuallyClosingRef.current = true
				window.history.replaceState(
					window.history.state,
					'',
					`${designStepUrl}${window.location.search}`,
				)
			}
			setIsOpen(false)
			bus.emit(RESET_NEXT_BUTTON_LOADING_EVENT)
			return
		}
		setIsOpen(open)
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				closeButtonPlacement="inline"
				className={cn(
					'desktop:py-[var(--sign-up-dialog-y-padding)]',
					shouldScrollDialogOverlay
						? 'desktop:overflow-y-auto'
						: 'desktop:overflow-y-hidden',
				)}
				style={
					{
						'--sign-up-dialog-y-padding': `${dialogVerticalPadding}px`,
					} as TSignUpDialogStyle
				}
				panelClassName={cn(
					'w-full rounded-none bg-transparent p-0 shadow-none desktop:relative desktop:left-auto desktop:top-auto desktop:mx-auto desktop:my-0 desktop:translate-x-0 desktop:translate-y-0',
					getVersionForClone({
						base: 'max-w-[min(976px,calc(100dvw-2.5rem))] desktop:w-[976px] desktop:max-w-[976px]',
						clone1: 'max-w-[min(350px,calc(100dvw-2.5rem))] desktop:w-[976px] desktop:max-w-[min(976px,calc(100dvw-2.5rem))]',
					}),
				)}
				overlayClassName="backdrop-blur-[20px]"
				toastContainer={
					<LazyToastContainer
						containerId="dialog"
						position="top-center"
						className="dialog-toast-container !pointer-events-auto !z-[10000]"
						toastClassName="!pointer-events-auto"
					/>
				}
				scrollAreaClassName="overflow-y-auto desktop:overflow-visible"
				onInteractOutside={(event) => event.preventDefault()}
				onPointerDownOutside={(event) => event.preventDefault()}
			>
				<div ref={setDialogBodyElement}>
					<SignUpContainer
						key={qrId ?? 'no-qr'}
						isOnboardingFlow
						isPopupMode
						locale={locale}
						user={null}
						hideSocials={hideSocials}
						hidePassword={hidePassword}
						defaultEmail={defaultEmail}
						previousEmail={previousEmail}
						onboardingQrId={qrId}
					/>
				</div>
			</DialogContent>
		</Dialog>
	)
}
